/* Whitetail Hill redesign — shared front-end.
   FAQ accordion · pack-aware protection math · product page (ladder, add-on,
   running total, sticky bar, plant-both) · cart drawer (free-ship bar,
   protection nudge, tree count). No dependencies. Every module no-ops when
   its markup is absent, so this is safe to load on every page.
   Rewritten 2026-09-16 for the Impulse 9.2.0 redesign; cart made protection-first 2026-09-18;
   2026-09-21 — PDP add-on now tracks the quantity stepper, and the cart's protect-first
   checkout gate was removed (see initCartRoot). */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* utilities                                                           */
  /* ------------------------------------------------------------------ */
  function money(cents) {
    return '$' + (Math.round(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent || el.getAttribute('content') || ''); } catch (e) { return null; }
  }
  function fill(tpl, map) {
    return String(tpl || '').replace(/\[(\w+)\]/g, function (m, k) { return (k in map) ? map[k] : m; });
  }
  function plural(n, one, many) { return n === 1 ? (one || 'tree') : (many || 'trees'); }
  var WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
  function word(n) { return n <= 10 ? WORDS[n] : String(n); }
  function packSize(v) {
    /* Titles look like "1", "5", "10 ($8.49/Tube)". First number only. */
    var m = String(v.title).match(/\d+/);
    var n = m ? parseInt(m[0], 10) : 1;
    return (isNaN(n) || n < 1) ? 1 : n;
  }

  /* ------------------------------------------------------------------ */
  /* Pack math — cheapest combination of pack variants covering qty.     */
  /* Never multiplies the single when a pack is cheaper. Unbounded       */
  /* knapsack; allows overshoot (a 25-pack for 23 trees) when cheaper.   */
  /* ------------------------------------------------------------------ */
  function cheapestCover(variants, qty) {
    var packs = (variants || []).filter(function (v) { return v.available !== false; })
      .map(function (v) { return { v: v, size: packSize(v), price: Number(v.price) }; })
      .filter(function (p) { return p.size > 0 && p.price > 0; })
      .sort(function (a, b) { return b.size - a.size; }); /* largest first so a price tie goes to the pack, not N singles */
    if (!packs.length || qty < 1) return null;
    var maxSize = packs.reduce(function (m, p) { return Math.max(m, p.size); }, 0);
    var limit = qty + maxSize;            /* overshoot allowed up to one extra largest pack */
    var best = new Array(limit + 1).fill(Infinity);
    var pick = new Array(limit + 1).fill(null);
    best[0] = 0;
    for (var n = 1; n <= limit; n++) {
      for (var i = 0; i < packs.length; i++) {
        var p = packs[i];
        if (p.size <= n && best[n - p.size] + p.price < best[n]) {
          best[n] = best[n - p.size] + p.price;
          pick[n] = i;
        }
      }
    }
    var target = -1;
    for (var t = qty; t <= limit; t++) {
      if (best[t] < Infinity && (target < 0 || best[t] < best[target])) target = t;
    }
    if (target < 0) return null;
    var counts = {};
    var cur = target;
    while (cur > 0) { var idx = pick[cur]; counts[idx] = (counts[idx] || 0) + 1; cur -= packs[idx].size; }
    var lines = Object.keys(counts).map(function (k) {
      var p = packs[k];
      return { id: p.v.id, title: p.v.title, size: p.size, count: counts[k], price: p.price, total: p.price * counts[k] };
    }).sort(function (a, b) { return b.size - a.size; });
    return {
      lines: lines,
      covered: target,
      total: best[target],
      perUnit: best[target] / target,
      /* "a 10-pack plus two singles" */
      describe: function () {
        var parts = lines.map(function (l) {
          if (l.size === 1) return (l.count === 1 ? 'one single' : word(l.count) + ' singles');
          return (l.count === 1 ? 'a ' : word(l.count) + ' ') + l.size + '-pack' + (l.count === 1 ? '' : 's');
        });
        if (parts.length === 1) return parts[0];
        return parts.slice(0, -1).join(', ') + ' plus ' + parts[parts.length - 1];
      }
    };
  }
  window.whcCheapestCover = cheapestCover; /* exposed for testing in preview */

  /* ------------------------------------------------------------------ */
  /* Cart helpers shared by PDP and drawer                               */
  /* ------------------------------------------------------------------ */
  function cartAdd(items) {
    return fetch((window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) { if (!r.ok) throw r; return r.json(); });
  }
  function refreshDrawer(open) {
    /* Mirror Impulse: fetch the ajax cart template and swap items + discounts in every
       cart root on the page (drawer and/or /cart page). */
    return fetch('/cart?view=ajax', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var tmp = document.createElement('div'); tmp.innerHTML = html;
        var items = tmp.querySelector('.cart__items');
        var disc = tmp.querySelector('.cart__discounts');
        var count = items ? parseInt(items.getAttribute('data-count'), 10) : 0;
        var sub = items ? parseInt(items.getAttribute('data-cart-subtotal'), 10) : 0;
        var roots = document.querySelectorAll('[data-whc-cart]');
        if (!roots.length) roots = document.querySelectorAll('#CartDrawer, form[data-location="page"]');
        roots.forEach(function (root) {
          var products = root.querySelector('[data-products]');
          if (products && items) products.innerHTML = items.outerHTML;
          var dwrap = root.querySelector('[data-discounts]');
          if (dwrap && disc) dwrap.innerHTML = disc.outerHTML;
          var subEl = root.querySelector('[data-subtotal]');
          if (subEl) subEl.textContent = money(sub);
          root.classList.toggle('is-empty', count === 0);
        });
        /* header bubble */
        document.querySelectorAll('.cart-link__bubble-num').forEach(function (n) { n.textContent = count; });
        document.querySelectorAll('.cart-link__bubble').forEach(function (b) { b.classList.toggle('cart-link__bubble--visible', count > 0); });
        document.dispatchEvent(new CustomEvent('whc:cart:refreshed', { detail: { count: count, subtotal: sub } }));
        var drawer = document.getElementById('CartDrawer');
        if (open && drawer && !drawer.classList.contains('is-active')) {
          var opener = document.querySelector('.js-drawer-open-cart, [data-drawer-open="cart"], .site-nav__link--cart');
          if (opener) opener.click();
        }
      });
  }

  /* ------------------------------------------------------------------ */
  /* FAQ accordion                                                       */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.whc-fq button');
    if (!btn) return;
    var item = btn.parentElement;
    var open = item.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  /* ------------------------------------------------------------------ */
  /* Product page                                                        */
  /* ------------------------------------------------------------------ */
  function initPDP() {
    var root = document.querySelector('[data-whc-pdp]');
    if (!root) return;
    var cfg = readJSON(root.querySelector('script[data-whc-pdp-config]')) || {};
    var prices = cfg.variantPrices || {};                 /* {variantId: cents} */
    var form = document.getElementById(cfg.formId) || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    /* form.id is shadowed by the <select name="id"> inside the form — always read the attribute */
    var formIdAttr = form.getAttribute('id');

    /* badges above title, if asked */
    var badges = root.querySelector('[data-whc-badges][data-move="true"]');
    var title = root.querySelector('.product-single__title');
    if (badges && title && title.parentNode) title.parentNode.insertBefore(badges, title);

    function qty() {
      var el = form.querySelector('input[name="quantity"]') || document.querySelector('input[name="quantity"][form="' + formIdAttr + '"]');
      var n = el ? parseInt(el.value, 10) : 1;
      return (isNaN(n) || n < 1) ? 1 : n;
    }
    function variantId() {
      var sel = form.querySelector('[name="id"]');
      return sel ? String(sel.value) : null;
    }
    function unitCents() {
      var id = variantId();
      if (id && prices[id] != null) return Number(prices[id]);
      var k = Object.keys(prices); return k.length ? Number(prices[k[0]]) : 0;
    }

    /* --- tier ladder (display only; Hulk applies the discount in cart) --- */
    var ladder = root.querySelector('[data-whc-ladder]');
    var tiers = ladder ? (readJSON(ladder.querySelector('script[data-whc-tiers]')) || []) : [];
    tiers = tiers.filter(function (t) { return t.qty > 0 && t.pct > 0; }).sort(function (a, b) { return a.qty - b.qty; });
    function tierFor(q) {
      var hit = null; tiers.forEach(function (t) { if (q >= t.qty) hit = t; }); return hit;
    }

    /* --- add-on --- */
    var addon = root.querySelector('[data-whc-addon]');
    var addonVariants = addon ? (readJSON(addon.querySelector('script[data-whc-addon-variants]')) || []) : [];
    var addonCfg = addon ? (readJSON(addon.querySelector('script[data-whc-addon-config]')) || {}) : {};
    var addonToggle = addon ? addon.querySelector('[data-whc-addon-toggle]') : null;
    var addonPick = null;

    /* --- running total in the button + sticky bar --- */
    var atcBtn = form.querySelector('[data-add-to-cart]');
    var totalEl = null;
    if (atcBtn && cfg.showTotal) {
      totalEl = document.createElement('span');
      totalEl.className = 'whc-atc-total';
      totalEl.setAttribute('data-whc-atc-total', '');
      atcBtn.appendChild(totalEl);
    }
    var sticky = root.querySelector('[data-whc-sticky]');

    function update() {
      var q = qty();
      var unit = unitCents();
      var tier = tierFor(q);
      var treeTotal = unit * q;
      var saved = 0;
      if (tier) { saved = Math.round(treeTotal * tier.pct / 100); treeTotal -= saved; }

      if (ladder) {
        ladder.querySelectorAll('[data-whc-tier]').forEach(function (row) {
          var tq = parseInt(row.getAttribute('data-whc-tier'), 10);
          row.classList.toggle('is-active', tier ? tq === tier.qty : tq === 0);
        });
        ladder.querySelectorAll('[data-whc-tier-price]').forEach(function (el) {
          var pct = parseFloat(el.getAttribute('data-whc-tier-price')) || 0;
          el.textContent = money(unit * (1 - pct / 100)) + (cfg.perTreeSuffix ? ' ' + cfg.perTreeSuffix : '');
        });
        var helper = ladder.querySelector('[data-whc-ladder-helper]');
        if (helper) {
          if (tier) {
            helper.textContent = fill(cfg.helperText, { qty: q, trees: plural(q, cfg.treeOne, cfg.treeMany), total: money(treeTotal), saved: money(saved), tier: tier.qty, pct: tier.pct });
            helper.hidden = false;
          } else { helper.hidden = true; }
        }
      }

      var addonTotal = 0;
      if (addon && addonVariants.length) {
        addonPick = cheapestCover(addonVariants, q);
        var on = addonToggle ? addonToggle.checked : true;
        if (addonPick) {
          var kits = addonPick.covered;
          addon.querySelectorAll('[data-whc-addon-qty]').forEach(function (el) { el.textContent = q; });
          var priceLine = addon.querySelector('[data-whc-addon-price]');
          var amap = { qty: q, trees: plural(q, cfg.treeOne, cfg.treeMany), kits: kits, kit_word: kits === 1 ? 'kit' : 'kits', per: money(addonPick.perUnit), total: money(addonPick.total), combo: addonPick.describe() };
          if (priceLine) priceLine.textContent = fill(addonCfg.priceLine, amap);
          var matched = addon.querySelector('[data-whc-addon-matched]');
          if (matched) matched.textContent = fill(addonCfg.matchedText, amap);
          if (on) addonTotal = addonPick.total;
        }
        addon.classList.toggle('is-on', on);
      }

      var grand = treeTotal + addonTotal;
      if (totalEl) totalEl.textContent = money(grand);
      if (sticky) {
        var sp = sticky.querySelector('[data-whc-sticky-price]');
        if (sp) sp.textContent = money(grand);
      }
      var pb = root.querySelector('[data-whc-plantboth]');
      if (pb) {
        pb.querySelectorAll('[data-whc-pb-qty]').forEach(function (el) { el.textContent = q; });
      }
    }

    document.addEventListener('change', function (e) {
      if (e.target.name === 'quantity' || e.target.name === 'id' || e.target === addonToggle) update();
    });
    document.addEventListener('input', function (e) { if (e.target.name === 'quantity') update(); });

    /* 2026-09-21 — quantity stepper sync.
       Impulse's +/- buttons set input.value and then call jQuery's .trigger('change'),
       which runs jQuery handlers only: no native event is dispatched, so the two document
       listeners above never fired and the add-on price/combo stayed stale until the shopper
       toggled the checkbox (a real change event). Catch the stepper clicks directly, and
       poll the value as a backstop so any other script that writes the field is covered too. */
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('.js-qty__adjust, .qty__adjust, [data-qty-adjust], .js-qty-adjust, .quantity__button')) {
        setTimeout(update, 0);
        setTimeout(update, 150);
      }
    });

    /* Impulse also changes the hidden select + price on variant change; poll for both. */
    var lastVid = variantId();
    var lastQty = qty();
    setInterval(function () {
      var v = variantId();
      var q = qty();
      if (v !== lastVid || q !== lastQty) { lastVid = v; lastQty = q; update(); }
    }, 200);
    update();

    /* --- submit: add protection first, then let the theme add the tree and open the drawer --- */
    var passthrough = false;
    document.addEventListener('submit', function (e) {
      if (e.target !== form) return;
      if (passthrough) { passthrough = false; return; }
      /* recompute against the live quantity: never trust a stale pick at submit time */
      update();
      var on = addon && addonToggle && addonToggle.checked && addonPick && addonPick.lines.length;
      if (!on) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var items = addonPick.lines.map(function (l) { return { id: Number(l.id), quantity: l.count }; });
      cartAdd(items).catch(function () { /* still add the tree */ }).then(function () {
        passthrough = true;
        if (form.requestSubmit) form.requestSubmit(); else form.querySelector('[type="submit"]').click();
      });
    }, true);

    /* --- sticky bar: show once the real button scrolls off (mobile) --- */
    if (sticky && atcBtn && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var visible = entries[0].isIntersecting;
        var below = entries[0].boundingClientRect.top > window.innerHeight; /* still under the fold */
        sticky.classList.toggle('is-visible', !visible && !below);
      }, { threshold: 0 });
      io.observe(atcBtn);
      var sbtn = sticky.querySelector('[data-whc-sticky-add]');
      if (sbtn) sbtn.addEventListener('click', function () {
        if (form.requestSubmit) form.requestSubmit(); else atcBtn.click();
      });
    }

    /* --- plant both: add the paired product at the same quantity --- */
    root.querySelectorAll('[data-whc-pb-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var vid = Number(btn.getAttribute('data-variant-id'));
        var match = btn.getAttribute('data-match-qty') === 'true';
        var q = match ? qty() : 1;
        if (!vid) return;
        btn.disabled = true; var old = btn.textContent; btn.textContent = btn.getAttribute('data-adding') || 'Adding…';
        cartAdd([{ id: vid, quantity: q }]).then(function () { return refreshDrawer(true); })
          .then(function () { btn.textContent = btn.getAttribute('data-added') || 'Added'; })
          .catch(function () { btn.textContent = old; })
          .then(function () { setTimeout(function () { btn.disabled = false; btn.textContent = old; }, 1800); });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart drawer + cart page                                             */
  /* Rewritten 2026-09-18 protection-first; the gate REMOVED 2026-09-21. */
  /*                                                                     */
  /* Checkout is never gated. The nudge above the line items offers the  */
  /* cheapest Survival Kit combo with a one-tap add; the Check Out button */
  /* always checks out. The old behaviour hid Check Out behind "Protect   */
  /* all N & Check Out" and pushed the shopper to a small skip link that  */
  /* opened a bottom sheet clipped inside the transformed drawer — the    */
  /* drawer looked frozen. The protect/skip/sheet handlers below are kept */
  /* as no-ops: their markup no longer renders, and they cost nothing if  */
  /* an old cached template still carries it.                             */
  /* One root per cart surface ([data-whc-cart]): the drawer, the /cart   */
  /* page, or both.                                                       */
  /* ------------------------------------------------------------------ */
  var SKIP_KEY = 'whc_skip_protection';
  function skipped() { try { return sessionStorage.getItem(SKIP_KEY) === '1'; } catch (e) { return false; } }
  function rememberSkip() { try { sessionStorage.setItem(SKIP_KEY, '1'); } catch (e) { /* private mode */ } }

  function initCartRoot(cart) {
    var cfg = readJSON(cart.querySelector('script[data-whc-cart-config]')) || {};
    var protVariants = readJSON(cart.querySelector('script[data-whc-protection-variants]')) || [];
    var threshold = Math.round((Number(cfg.threshold) || 0) * 100);
    var state = { pick: null, need: 0, trees: 0 };

    function scan() {
      var wrap = cart.querySelector('.cart__items');
      var items = cart.querySelectorAll('.cart__items .cart__item');
      var trees = 0, protectedCount = 0, count = 0, subtotal = 0;
      if (wrap) { count = parseInt(wrap.getAttribute('data-count'), 10) || 0; subtotal = parseInt(wrap.getAttribute('data-cart-subtotal'), 10) || 0; }
      items.forEach(function (it) {
        var q = parseInt(it.getAttribute('data-whc-qty'), 10) || 0;
        /* data-whc-tree = trees per unit: 1 for a tree, 100 for a 100-tree bulk pack, 0 for anything else */
        var per = parseInt(it.getAttribute('data-whc-tree'), 10) || 0;
        if (per > 0) trees += per * q;
        var ps = parseInt(it.getAttribute('data-whc-protection'), 10) || 0;
        if (ps > 0) protectedCount += ps * q;
      });
      return { trees: trees, protectedCount: protectedCount, count: count, subtotal: subtotal };
    }

    function mapFor(s, pick) {
      var need = Math.max(0, s.trees - s.protectedCount);
      return {
        count: s.trees,
        trees: plural(s.trees, cfg.treeSingular, cfg.treePlural).toLowerCase(),
        Trees: plural(s.trees, cfg.treeSingular, cfg.treePlural),
        need: need,
        need_trees: plural(need, cfg.treeSingular, cfg.treePlural).toLowerCase(),
        protected: s.protectedCount,
        kits: need === 1 ? (cfg.kitSingular || 'kit') : (cfg.kitPlural || 'kits'),
        combo: pick ? pick.describe() : '',
        total: pick ? money(pick.total) : '',
        per: pick ? money(pick.perUnit) : ''
      };
    }

    function render() {
      var s = scan();
      var need = Math.max(0, s.trees - s.protectedCount);
      var pick = (need > 0 && protVariants.length) ? cheapestCover(protVariants, need) : null;
      var unprotected = !!(s.trees > 0 && need > 0 && pick);
      state.pick = pick; state.need = need; state.trees = s.trees;
      var map = mapFor(s, pick);

      /* title */
      cart.querySelectorAll('[data-whc-cart-title]').forEach(function (t) {
        t.textContent = s.trees > 0 ? fill(cfg.titleWithTrees, { count: s.trees, trees: map.Trees }) : cfg.title;
      });

      /* free-ship bar */
      var bar = cart.querySelector('[data-whc-shipbar]');
      if (bar && threshold > 0) {
        var pct = Math.max(0, Math.min(100, s.subtotal / threshold * 100));
        var fillEl = bar.querySelector('[data-whc-shipbar-fill]');
        if (fillEl) fillEl.style.width = pct + '%';
        var txt = bar.querySelector('[data-whc-shipbar-text]');
        if (txt) txt.textContent = s.subtotal >= threshold ? cfg.shipDone : fill(cfg.shipText, { amount: money(threshold - s.subtotal) });
        bar.classList.toggle('is-done', s.subtotal >= threshold);
        bar.hidden = s.count === 0;
      }

      /* protection nudge (sits above the line items) — an offer, never a gate */
      var nudge = cart.querySelector('[data-whc-nudge]');
      if (nudge) {
        if (unprotected) {
          var nt = nudge.querySelector('[data-whc-nudge-title]');
          if (nt) nt.textContent = fill(s.protectedCount > 0 ? cfg.nudgeTitlePartial : cfg.nudgeTitle, map);
          var nb = nudge.querySelector('[data-whc-nudge-body]');
          if (nb) nb.innerHTML = fill(nudge.getAttribute('data-body-template') || '', map);
          var btn = nudge.querySelector('[data-whc-nudge-add]');
          if (btn) { btn.textContent = fill(cfg.nudgeButton, map); btn.__pick = pick; btn.disabled = false; }
          nudge.hidden = false;
        } else {
          nudge.hidden = true;
        }
      }

      /* Checkout is never gated (2026-09-21). Force the plain button visible and keep any
         leftover protect/skip markup hidden, so a stale cached template can't resurrect it. */
      var coPlain = cart.querySelector('[data-whc-checkout]');
      var coProtect = cart.querySelector('[data-whc-checkout-protect]');
      var coSkip = cart.querySelector('[data-whc-checkout-skip]');
      if (coProtect) coProtect.hidden = true;
      if (coSkip) coSkip.hidden = true;
      if (coPlain) { coPlain.hidden = false; coPlain.disabled = false; }
      cart.classList.remove('whc-cart--busy');
      cart.classList.toggle('whc-cart--unprotected', unprotected);

      var footer = cart.querySelector('[data-whc-cart-footer]');
      if (footer) footer.classList.toggle('is-empty', s.count === 0);
      var sw = cart.querySelector('[data-whc-shipwin]');
      if (sw) sw.hidden = s.trees === 0;
    }

    /* Checkout. AOD Order Limits (when installed) redirects every button[name=checkout] on
       non-cart pages to /cart, and its own drawer hook validates but never proceeds. So these
       are plain buttons: we ask AOD to validate the cart ourselves (it shows its own popup on
       failure) and go to checkout only when it passes — or straight away if AOD is absent. */
    function gotoCheckout() {
      var base = (window.theme && theme.routes && theme.routes.cartPage) ? theme.routes.cartPage.replace(/\/cart$/, '') : '';
      window.location.href = base + '/checkout';
    }
    function termsOk() {
      var req = cart.querySelector('[data-terms-required="true"]');
      if (!req) return true;
      var terms = cart.querySelector('.cart__terms-checkbox');
      var msg = cart.querySelector('[data-whc-terms-msg]');
      if (terms && !terms.checked) { if (msg) msg.hidden = false; return false; }
      if (msg) msg.hidden = true;
      return true;
    }
    var busy = false;
    function checkout() {
      if (busy) return;
      busy = true;
      cart.classList.add('whc-cart--busy');
      var released = false;
      var guard = null;
      var release = function () {
        if (released) return;
        released = true;
        busy = false;
        cart.classList.remove('whc-cart--busy');
        if (guard) { clearTimeout(guard); guard = null; }
      };
      /* A hung request must never leave the button dead: always let the shopper try again. */
      guard = setTimeout(release, 6000);
      if (typeof window.aodValidate !== 'function') { gotoCheckout(); return; }
      fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); })
        .then(function (c) {
          var ok = false;
          try { ok = window.aodValidate(c, true, false); } catch (err) { ok = true; }
          if (ok) gotoCheckout(); else release();
        })
        .catch(function () { gotoCheckout(); });
    }
    function addProtection(pick) {
      if (!pick) return Promise.resolve();
      var items = pick.lines.map(function (l) { return { id: Number(l.id), quantity: l.count }; });
      return cartAdd(items).then(function () { return refreshDrawer(false); });
    }

    cart.addEventListener('click', function (e) {
      var t = e.target;
      var el;

      if ((el = t.closest('[data-whc-checkout]'))) {
        e.preventDefault();
        if (!termsOk()) return;
        checkout();
        return;
      }
      /* Legacy markup only — these no longer render. Both simply check out. */
      if ((el = t.closest('[data-whc-checkout-protect]')) || (el = t.closest('[data-whc-checkout-skip]'))) {
        e.preventDefault();
        if (!termsOk()) return;
        checkout();
        return;
      }
      if ((el = t.closest('[data-whc-nudge-add]'))) {
        if (!el.__pick) return;
        e.preventDefault();
        el.disabled = true;
        var o3 = el.textContent;
        el.textContent = el.getAttribute('data-adding') || 'Adding…';
        addProtection(el.__pick)
          .then(function () { document.dispatchEvent(new CustomEvent('whc:protection:added', { detail: { from: 'nudge', need: state.need } })); })
          .catch(function () { el.textContent = o3; })
          .then(function () { el.disabled = false; });
      }
    });

    /* Re-render whenever Impulse (or we) swap the items markup. */
    var products = cart.querySelector('[data-products]');
    if (products && 'MutationObserver' in window) {
      new MutationObserver(function () { render(); }).observe(products, { childList: true, subtree: false });
    }
    ['cart:updated', 'cart:build', 'ajaxProduct:added', 'whc:cart:refreshed'].forEach(function (ev) {
      document.addEventListener(ev, function () { setTimeout(render, 50); });
    });
    render();
  }

  function initCart() {
    document.querySelectorAll('[data-whc-cart]').forEach(initCartRoot);
  }

  /* ------------------------------------------------------------------ */
  /* Why Whitetail Hill swipe deck — live counter + dots                 */
  /* ------------------------------------------------------------------ */
  function initWhy() {
    document.querySelectorAll('[data-whc-why]').forEach(function (root) {
      var track = root.querySelector('[data-whc-why-track]');
      var cards = root.querySelectorAll('[data-whc-why-card]');
      var dots = root.querySelectorAll('.whc-why__dot');
      var cur = root.querySelector('[data-whc-why-current]');
      if (!track || !cards.length || !('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.intersectionRatio < 0.6) return;
          var i = Array.prototype.indexOf.call(cards, e.target);
          if (cur) cur.textContent = i + 1;
          dots.forEach(function (d, j) { d.classList.toggle('is-active', j === i); });
        });
      }, { root: track, threshold: [0.6] });
      cards.forEach(function (c) { io.observe(c); });
    });
  }


  /* ------------------------------------------------------------------ */
  /* Ship-window block (cart footer): state dropdown → ship window        */
  /* Shares the chosen state with the tree picker via localStorage        */
  /* "whc_state" and a "whc:state" event.                                 */
  /* ------------------------------------------------------------------ */
  var STATE_KEY = 'whc_state';
  function getState() { try { return localStorage.getItem(STATE_KEY) || ''; } catch (e) { return ''; } }
  function setState(v) { try { if (v) localStorage.setItem(STATE_KEY, v); else localStorage.removeItem(STATE_KEY); } catch (e) { /* private mode */ } }
  window.whcGetState = getState;

  function initShipWin() {
    var blocks = document.querySelectorAll('[data-whc-shipwin]');
    if (!blocks.length) return;
    blocks.forEach(function (el) {
      var windows = [];
      String(el.getAttribute('data-windows') || '').split(/\r?\n/).forEach(function (line) {
        var parts = line.split('|');
        if (parts.length < 2) return;
        var dates = parts[0].trim();
        var codes = parts[1].split(',').map(function (c) { return c.trim().toUpperCase(); }).filter(Boolean);
        if (dates && codes.length) windows.push({ dates: dates, codes: codes });
      });
      var noship = String(el.getAttribute('data-noship') || '').split(',').map(function (c) { return c.trim().toUpperCase(); }).filter(Boolean);
      var sel = el.querySelector('[data-whc-shipwin-state]');
      var out = el.querySelector('[data-whc-shipwin-out]');
      if (!sel || !out) return;
      function nameOf(code) { var o = sel.querySelector('option[value="' + code + '"]'); return o ? o.textContent : code; }
      function render(code) {
        el.classList.remove('is-set', 'is-noship');
        if (!code) { out.textContent = out.getAttribute('data-default') || ''; return; }
        var map = { state: nameOf(code), code: code, window: '' };
        var tpl;
        if (noship.indexOf(code) >= 0) { tpl = el.getAttribute('data-tpl-noship'); el.classList.add('is-noship'); }
        else {
          var w = null; windows.forEach(function (x) { if (!w && x.codes.indexOf(code) >= 0) w = x; });
          if (w) { map.window = w.dates; tpl = el.getAttribute('data-tpl-result'); }
          else tpl = el.getAttribute('data-tpl-other');
        }
        el.classList.add('is-set');
        out.textContent = fill(tpl || '', map);
      }
      var cur = getState();
      if (cur) sel.value = cur;
      render(sel.value);
      sel.addEventListener('change', function () {
        setState(sel.value);
        render(sel.value);
        document.dispatchEvent(new CustomEvent('whc:state', { detail: { state: sel.value, source: 'cart' } }));
      });
      document.addEventListener('whc:state', function (e) {
        var v = (e.detail && e.detail.state) || '';
        if (v !== sel.value) { sel.value = v; render(sel.value); }
      });
    });
  }


  /* ------------------------------------------------------------------ *
   * State-matched proof (2026-09-19, chat 7)
   * Renders customer quotes filtered by the state the shopper is planting in.
   * Reads the shared state key written by the cart ship-window block (STATE_KEY /
   * the `whc:state` event above). The state is a FILTER, never a label: each quote
   * always carries its own reviewer's state, only the heading changes.
   * Markup: snippets/whc-proof-local.liquid + snippets/whc-proof-data.liquid.
   * ------------------------------------------------------------------ */
  var GEO_KEY = 'whc_geo_state';
  var SEED_KEY = 'whc_proof_seed';

  function initProofLocal() {
    var blocks = document.querySelectorAll('[data-whc-proof]');
    if (!blocks.length) return;
    var quotes = readJSON(document.querySelector('script[data-whc-proof-quotes]')) || [];
    var stats = readJSON(document.querySelector('script[data-whc-proof-stats]')) || {};
    if (!quotes.length) return;

    var cfg = blocks[0];

    /* code -> full state name, from the "State names" setting ("AL:Alabama,AK:Alaska,...") */
    var names = {};
    String(cfg.getAttribute('data-states') || '').split(',').forEach(function (pair) {
      var i = pair.indexOf(':');
      if (i < 0) return;
      var c = pair.slice(0, i).trim().toUpperCase();
      var n = pair.slice(i + 1).trim();
      if (c && n) names[c] = n;
    });

    /* ship-window zones, one per line: "zone | dates | CODE,CODE,..." */
    var zones = [];
    String(cfg.getAttribute('data-zones') || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split('|');
      if (parts.length < 3) return;
      var key = parts[0].trim();
      var codes = parts[2].split(',').map(function (c) { return c.trim().toUpperCase(); }).filter(Boolean);
      if (key && codes.length) zones.push({ key: key, codes: codes });
    });
    function zoneOf(code) {
      var z = '';
      zones.forEach(function (x) { if (!z && x.codes.indexOf(code) >= 0) z = x.key; });
      return z;
    }

    /* One seed per session so the same visitor keeps seeing the same picks page to page. */
    var seed = 0;
    try { seed = parseInt(sessionStorage.getItem(SEED_KEY), 10) || 0; } catch (e) { seed = 0; }
    if (!seed) {
      seed = Math.floor(Math.random() * 99999) + 1;
      try { sessionStorage.setItem(SEED_KEY, String(seed)); } catch (e) { /* private mode */ }
    }

    function score(q) { return (q.t === 'A' ? 100 : 0) + (q.r || 0) * 5 + (q.p ? 3 : 0); }
    function rank(list) {
      return list.slice().map(function (q, i) {
        return { q: q, k: score(q) * 1000 - ((seed * (i + 7)) % 997) };
      }).sort(function (a, b) { return b.k - a.k; }).map(function (x) { return x.q; });
    }

    var byState = {};
    quotes.forEach(function (q) {
      var s = String(q.s || '').toUpperCase();
      if (!s) return;
      if (!byState[s]) byState[s] = [];
      byState[s].push(q);
    });
    var nationals = quotes.filter(function (q) { return q.n; });

    /* Guessed from IP. Deliberately NOT written to whc_state and never dispatched as
       `whc:state` — a guess must not fill in the shopper's own ship-window answer. */
    var geoState = '';

    function pickFor(code) {
      if (code && byState[code] && byState[code].length) return { tier: 'state', list: rank(byState[code]) };
      if (code) {
        var z = zoneOf(code);
        var pool = [];
        if (z) {
          Object.keys(byState).forEach(function (s) {
            if (s !== code && zoneOf(s) === z) pool = pool.concat(byState[s]);
          });
        }
        if (pool.length) return { tier: 'zone', list: rank(pool) };
      }
      if (nationals.length) return { tier: 'national', list: rank(nationals) };
      return { tier: 'national', list: rank(quotes) };
    }

    function commas(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

    function quoteNode(q, showPhoto) {
      var card = document.createElement('div');
      card.className = 'whc-proof__q';
      if (showPhoto && q.p) {
        var img = document.createElement('img');
        img.className = 'whc-proof__photo';
        img.src = q.p;
        img.alt = '';
        img.setAttribute('loading', 'lazy');
        img.width = 56;
        img.height = 56;
        img.addEventListener('error', function () {
          if (img.parentNode) img.parentNode.removeChild(img);
        });
        card.appendChild(img);
      }
      var body = document.createElement('div');
      body.className = 'whc-proof__body';
      var r = Math.max(0, Math.min(5, parseInt(q.r, 10) || 0));
      if (r) {
        var st = document.createElement('div');
        st.className = 'whc-proof__stars';
        st.textContent = new Array(r + 1).join('★');
        st.setAttribute('aria-label', r + ' out of 5 stars');
        body.appendChild(st);
      }
      var txt = document.createElement('p');
      txt.className = 'whc-proof__text';
      txt.textContent = q.q;
      body.appendChild(txt);
      var who = document.createElement('div');
      who.className = 'whc-proof__who';
      var place = [q.c, q.s].filter(Boolean).join(', ');
      who.textContent = '— ' + q.a + (place ? ', ' + place : '');
      body.appendChild(who);
      card.appendChild(body);
      return card;
    }

    function renderOne(el) {
      var chosen = getState();
      var guessed = !chosen && !!geoState;
      var code = chosen || geoState;
      var res = pickFor(code);
      var lim = parseInt(el.getAttribute('data-limit'), 10) || 3;
      var list = res.list.slice(0, lim);
      var listEl = el.querySelector('[data-whc-proof-list]');
      var headEl = el.querySelector('[data-whc-proof-heading]');
      var countEl = el.querySelector('[data-whc-proof-count]');
      if (!listEl || !list.length) { el.hidden = true; return; }

      /* A guessed state filters the quotes but never names the shopper's state back at them. */
      var tier = res.tier;
      if (guessed) tier = (el.getAttribute('data-geo-heading') === 'zone') ? 'zone' : 'national';

      if (headEl) {
        var tpl = tier === 'state' ? el.getAttribute('data-h-state')
          : tier === 'zone' ? el.getAttribute('data-h-zone')
            : el.getAttribute('data-h-national');
        headEl.textContent = fill(tpl || '', { state: names[code] || code || '', code: code || '' });
      }

      while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
      var photos = el.getAttribute('data-photos') === '1';
      list.forEach(function (q) { listEl.appendChild(quoteNode(q, photos)); });

      if (countEl) {
        var min = parseInt(el.getAttribute('data-min-orders'), 10) || 0;
        var st2 = (!guessed && code) ? stats[code] : null;
        if (st2 && st2.o >= min) {
          countEl.textContent = fill(el.getAttribute('data-count') || '', {
            orders: commas(st2.o),
            trees: commas(st2.t),
            state: names[code] || code,
            code: code,
            season: st2.y || ''
          });
        } else {
          countEl.textContent = el.getAttribute('data-count-fallback') || '';
        }
        countEl.hidden = !countEl.textContent;
      }

      el.classList.remove('whc-proof--state', 'whc-proof--zone', 'whc-proof--national');
      el.classList.add('whc-proof--' + tier);
      el.hidden = false;
    }

    function renderAll() {
      blocks = document.querySelectorAll('[data-whc-proof]');
      blocks.forEach(renderOne);
    }

    function loadGeo(done) {
      var called = false;
      function finish() { if (!called) { called = true; done(); } }
      if (cfg.getAttribute('data-geo') !== '1') return finish();
      if (getState()) return finish();
      var cached = null;
      try { cached = sessionStorage.getItem(GEO_KEY); } catch (e) { cached = null; }
      if (cached !== null) { geoState = cached; return finish(); }
      var url = cfg.getAttribute('data-geo-url');
      if (!url || typeof fetch !== 'function') return finish();
      var field = cfg.getAttribute('data-geo-field') || 'region';
      var timer = setTimeout(finish, 2500);
      fetch(url, { mode: 'cors', credentials: 'omit' }).then(function (r) {
        return r.ok ? r.json() : null;
      }).then(function (j) {
        var code = '';
        if (j) {
          var country = String(j.country_code || j.country || 'US').toUpperCase();
          if (country === 'US' || country === 'USA' || country === 'UNITED STATES') {
            var raw = String(j[field] == null ? '' : j[field]).trim();
            if (/^[A-Za-z]{2}$/.test(raw)) {
              code = raw.toUpperCase();
            } else if (raw) {
              Object.keys(names).forEach(function (c) {
                if (!code && names[c].toLowerCase() === raw.toLowerCase()) code = c;
              });
            }
          }
        }
        if (code && !names[code]) code = '';
        geoState = code;
        try { sessionStorage.setItem(GEO_KEY, code); } catch (e) { /* private mode */ }
      })['catch'](function () { /* blocked, offline or rate-limited — national picks stand */ })
        .then(function () { clearTimeout(timer); finish(); });
    }

    renderAll();
    loadGeo(function () { if (geoState) renderAll(); });
    document.addEventListener('whc:state', renderAll);
    document.addEventListener('whc:cart:refreshed', renderAll);
  }
  function boot() { initPDP(); initCart(); initWhy(); initShipWin(); initProofLocal(); }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
